import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import useAuthStore from '../../../core/stores/authStore';
import { getSettlementService } from '../../../domains/settlement';
import styles from './SettlementManagement.module.css';
import { pickArray, pickNum, AMOUNT_KEYS, isSettleable, toDayMap } from './settlementData';

// ── 날짜 헬퍼 ──
const formatDate = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const fmt = n => Number(n || 0).toLocaleString('ko-KR');

// ── 채널 정의 (Figma 409:21965 색상) ──
const CHANNELS = [
  { key: 'app_payment', label: '앱 결제', drawerLabel: '앱 결제 (약값)', color: '#34C759' },
  { key: 'pharma_coupon', label: '제약사 쿠폰', drawerLabel: '제약사 쿠폰', color: '#007AFF' },
  { key: 'paypay_point', label: '팜페이포인트', drawerLabel: '팜페이포인트', color: '#F79103' },
];

// ── 드로어 응답 방어적 매핑 (필드명 미확정) ──
const maskName = n => (n ? `${String(n)[0]}OO` : '');
const itemLabel = (item, ch) => {
  const coupon = item.coupon_name ?? item.couponName ?? item.campaign_name;
  if (ch === 'pharma_coupon' && coupon) return coupon;
  const name = item.patient_name ?? item.user_name ?? item.customer_name ?? item.member_name ?? item.name;
  if (name) return `${maskName(name)} 처방 결제`;
  return coupon ?? item.title ?? item.description ?? item.memo ?? '-';
};
const toChannelResult = (res, ch) => {
  const d = res?.data ?? res;
  const list = pickArray(d).filter(it => isSettleable(ch, it));
  const items = list.map(it => ({ label: itemLabel(it, ch), amount: pickNum(it, AMOUNT_KEYS) ?? 0 }));
  return {
    items,
    count: items.length,
    total: items.reduce((s, i) => s + i.amount, 0),
  };
};

const SettlementManagement = () => {
  const { pharmacy } = useAuthStore();

  const today = useMemo(() => startOfDay(new Date()), []);
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [channelFilter, setChannelFilter] = useState('all');

  // ── 월별 일자×채널 집계 (GET /settlement/daily, v2 I-2). 같은 달은 react-query 캐시 재사용 ──
  const monthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  const isFutureMonth = new Date(currentYear, currentMonth - 1, 1) > today;
  const { data: monthly } = useQuery({
    queryKey: ['settlement-daily', pharmacy?.id, monthKey],
    enabled: !!pharmacy?.id && !isFutureMonth,
    queryFn: async () => {
      const res = await getSettlementService().getDaily(monthKey);
      if (res?.result_code !== '200' && res?.resultCode !== 200) throw new Error(res?.result_msg ?? '정산 캘린더 조회 실패');
      return res.data;
    },
  });
  const daily = useMemo(() => toDayMap(monthly?.days), [monthly]);

  // ── 날짜 드로어 ──
  const [selectedDate, setSelectedDate] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [expanded, setExpanded] = useState({});

  // ── 월 이동 ──
  const goToPrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear(y => y - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };
  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear(y => y + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  // ── 캘린더 주 단위 그리드 (전·후월 포함) ──
  const calendarWeeks = useMemo(() => {
    const first = new Date(currentYear, currentMonth - 1, 1);
    const last = new Date(currentYear, currentMonth, 0);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const weeks = [];
    for (let cur = new Date(start); cur <= last || cur.getDay() !== 0; ) {
      if (cur.getDay() === 0) weeks.push([]);
      const date = new Date(cur);
      const inMonth = date.getMonth() === currentMonth - 1;
      const isFuture = date > today;
      // 응답은 해당 월만 → 전·후월 칸은 흐리게 비워 둠
      const data = inMonth && !isFuture ? daily[formatDate(date)] ?? null : null;
      weeks[weeks.length - 1].push({
        date,
        inMonth,
        isFuture,
        isToday: date.getTime() === today.getTime(),
        data,
      });
      cur.setDate(cur.getDate() + 1);
    }
    return weeks;
  }, [currentYear, currentMonth, today, daily]);

  // ── 주별 합계: 서버 weeks 는 월~일 기준이라 화면 주(일~토)와 달라 days 로 계산 ──
  const sumDays = days =>
    days.reduce(
      (acc, d) => {
        if (!d.inMonth || !d.data) return acc;
        acc.has = true;
        acc.total += d.data.total;
        CHANNELS.forEach(c => (acc[c.key] += d.data[c.key]));
        return acc;
      },
      {
        has: false,
        total: 0,
        app_payment: 0,
        pharma_coupon: 0,
        paypay_point: 0,
      }
    );
  const weekSums = calendarWeeks.map(sumDays);
  const monthSum = monthly?.month_total ? { has: true, ...monthly.month_total } : { has: false };

  // ── KPI: month_total. 지급 완료/예정은 지급 정책 미정(payout_policy_defined=false)이라 비움 ──
  const kpiCards = [
    { key: 'total', label: '이번 달 정산 총액' },
    { key: 'app_payment', label: '앱 결제 (약 값)' },
    { key: 'pharma_coupon', label: '제약사 쿠폰' },
    { key: 'paypay_point', label: '팜페이포인트' },
  ].map(c => ({ ...c, amount: monthSum[c.key] }));

  // ── 날짜 드로어 데이터 (기존 서비스 메서드, start=end=선택일) ──
  const openDrawer = async cell => {
    setSelectedDate(cell);
    setDrawerData(null);
    setExpanded({});
    const empty = { items: [], count: 0, total: 0 };
    if (!pharmacy?.id) {
      setDrawerData({ app_payment: empty, pharma_coupon: empty, paypay_point: empty });
      return;
    }
    const service = getSettlementService();
    const day = formatDate(cell.date);
    const [pg, coupon, point] = await Promise.allSettled([
      service.getPgPayments(pharmacy.id, day, day),
      // 달력(/settlement/daily)과 같은 범위: 본사 발행분, 포인트 USE
      service.getCouponUsages(pharmacy.id, day, day, { issuer_type: 'hq' }),
      service.getPointTransactions(pharmacy.id, day, day, { issuer_type: 'hq', transaction_type: 'USE' }),
    ]);
    const map = (r, ch) => (r.status === 'fulfilled' ? toChannelResult(r.value, ch) : empty);
    setDrawerData({
      app_payment: map(pg, 'app_payment'),
      pharma_coupon: map(coupon, 'pharma_coupon'),
      paypay_point: map(point, 'paypay_point'),
    });
  };
  const closeDrawer = () => setSelectedDate(null);

  const visibleChannels = channelFilter === 'all' ? CHANNELS : CHANNELS.filter(c => c.key === channelFilter);
  const isFiltered = channelFilter !== 'all';

  const renderAmounts = (data, withTotal) =>
    data ? (
      <div className={styles.cellInfo}>
        {withTotal && !isFiltered && <span className={styles.cellTotal}>{fmt(data.total)}</span>}
        <div className={styles.cellDetail}>
          {visibleChannels.map(c => (
            <span key={c.key} style={{ color: c.color }}>
              {fmt(data[c.key])}
            </span>
          ))}
        </div>
      </div>
    ) : (
      <div className={styles.cellInfo}>
        <span className={styles.cellEmpty}>—</span>
      </div>
    );

  const drawerTotal = drawerData ? CHANNELS.reduce((s, c) => s + drawerData[c.key].total, 0) : 0;

  return (
    <div className={styles.page}>
      {/* ─── KPI (468:26732) ─── */}
      <div className={styles.kpiGrid}>
        {kpiCards.map(card => {
          return (
            <div key={card.key} className={styles.kpiCard}>
              <div className={styles.kpiTop}>
                <span className={styles.kpiLabel}>{card.label}</span>
              </div>
              <div className={styles.kpiValue}>{card.amount == null ? '-' : `${fmt(card.amount)}원`}</div>
              <div className={styles.kpiSub}>
                <div className={styles.kpiSubRow}>
                  <span className={styles.kpiSubLabel}>지급 완료</span>
                  <span className={styles.kpiSubValue}>-</span>
                </div>
                <div className={styles.kpiSubRow}>
                  <span className={styles.kpiSubLabel}>지급 예정</span>
                  <span className={styles.kpiSubValue}>-</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── 캘린더 (468:26829) ─── */}
      <div className={styles.calendar}>
        <div className={styles.calendarHead}>
          <div className={styles.monthNav}>
            <h2 className={styles.monthTitle}>
              {currentYear}년 {currentMonth}월
            </h2>
            <div className={styles.monthArrows}>
              <button type="button" className={styles.arrowBtn} onClick={goToPrevMonth} aria-label="이전 달">
                <ChevronLeft size={16} />
              </button>
              <button type="button" className={styles.arrowBtn} onClick={goToNextMonth} aria-label="다음 달">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <div className={styles.filter}>
            <button
              type="button"
              className={`${styles.filterItem} ${channelFilter === 'all' ? styles.filterItemActive : ''}`}
              onClick={() => setChannelFilter('all')}
            >
              전체
            </button>
            {CHANNELS.map(c => (
              <button
                key={c.key}
                type="button"
                className={`${styles.filterItem} ${channelFilter === c.key ? styles.filterItemActive : ''}`}
                onClick={() => setChannelFilter(c.key)}
              >
                <span className={styles.dot} style={{ background: c.color }} />
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`${styles.grid} ${isFiltered ? styles.gridFiltered : ''}`}>
          {DAY_LABELS.map(label => (
            <div key={label} className={styles.th}>
              {label}
            </div>
          ))}
          <div className={`${styles.th} ${styles.sumTh}`}>합계</div>

          {calendarWeeks.map((week, wi) => (
            <React.Fragment key={wi}>
              {week.map(cell => {
                const clickable = cell.inMonth && !cell.isFuture;
                const selected = selectedDate && selectedDate.date.getTime() === cell.date.getTime();
                const sunday = cell.date.getDay() === 0;
                return (
                  <div
                    key={cell.date.getTime()}
                    className={[
                      styles.cell,
                      !cell.inMonth ? styles.cellOther : '',
                      clickable ? styles.cellClickable : '',
                      selected ? styles.cellSelected : '',
                    ].join(' ')}
                    onClick={clickable ? () => openDrawer(cell) : undefined}
                  >
                    <div className={`${styles.cellHead} ${cell.isToday ? styles.cellHeadToday : ''}`}>
                      <span className={styles.dateGroup}>
                        <span className={`${styles.cellDate} ${sunday ? styles.sunday : ''}`}>
                          {cell.date.getDate()}
                        </span>
                        {cell.isToday && (
                          <span className={styles.todayMark}>
                            오늘
                            <span className={styles.todayDot} />
                          </span>
                        )}
                      </span>
                    </div>
                    {renderAmounts(cell.data, true)}
                  </div>
                );
              })}
              <div className={`${styles.sumCol} ${wi === calendarWeeks.length - 1 && !isFiltered ? styles.sumColLast : ''}`}>
                <div className={styles.sumCell}>
                  <div className={styles.cellHead}>
                    <span className={styles.cellDate}>&nbsp;</span>
                  </div>
                  {renderAmounts(weekSums[wi].has ? weekSums[wi] : null, true)}
                </div>
              </div>
            </React.Fragment>
          ))}

          {isFiltered && (
            <>
              <div className={styles.monthTotalSpacer} />
              <div className={`${styles.sumCol} ${styles.sumColLast} ${styles.monthTotal}`}>
                <div className={styles.sumCell}>{renderAmounts(monthSum.has ? monthSum : null, false)}</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ─── 날짜 드로어 (422:14253 / 431:15153) ─── */}
      {selectedDate && (
        <div className={styles.drawerBackdrop} onClick={closeDrawer}>
          <aside className={styles.drawer} onClick={e => e.stopPropagation()}>
            <div className={styles.drawerHead}>
              <div className={styles.drawerHeadText}>
                <p className={styles.drawerDate}>
                  {selectedDate.date.getMonth() + 1}월 {selectedDate.date.getDate()}일 (
                  {DAY_LABELS[selectedDate.date.getDay()]})
                </p>
                <p className={styles.drawerTotal}>{drawerData ? `${fmt(drawerTotal)}원` : '-'}</p>
              </div>
              <button type="button" className={styles.drawerClose} onClick={closeDrawer} aria-label="닫기">
                <X size={16} />
              </button>
            </div>

            {drawerData && (
              <div className={styles.drawerBody}>
                {CHANNELS.map(c => {
                  const ch = drawerData[c.key];
                  const isOpen = expanded[c.key];
                  const shown = isOpen ? ch.items : ch.items.slice(0, 3);
                  const more = ch.count - shown.length;
                  return (
                    <section key={c.key} className={styles.block}>
                      <div className={styles.blockTitle}>
                        <span className={styles.blockDot} style={{ background: c.color }} />
                        <span className={styles.blockLabel}>{c.drawerLabel}</span>
                        {ch.count > 0 && <span className={styles.blockCount}>{ch.count}</span>}
                        <span className={styles.blockSum}>{fmt(ch.total)}원</span>
                      </div>
                      <div className={styles.blockList}>
                        {ch.items.length === 0 ? (
                          <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>내역 없음</span>
                          </div>
                        ) : (
                          shown.map((it, i) => (
                            <div key={i} className={styles.infoRow}>
                              <span className={styles.infoLabel}>{it.label}</span>
                              <span className={styles.infoValue}>{fmt(it.amount)}원</span>
                            </div>
                          ))
                        )}
                        {!isOpen && more > 0 && ch.items.length > 3 && (
                          <button
                            type="button"
                            className={styles.moreBtn}
                            onClick={() => setExpanded(p => ({ ...p, [c.key]: true }))}
                          >
                            {more}건 더보기
                            <ChevronDown size={18} />
                          </button>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}

            <div className={styles.drawerFoot}>
              <p className={styles.policy}>
                앱결제 팜페이포인트는 크레소티가 정산하며, 제약사 쿠폰은 제약사 재원을 크레소티가 대행 정산합니다.
                <br />
                정산일 매월 25일
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default SettlementManagement;
